import { addMinutes, format, isBefore, parse } from 'date-fns';

/**
 * Generate available times
 *
 * @param {string} start - start time (format: 'HH:mm')
 * @param {string} end - end time (format: 'HH:mm')
 * @param {number} defaultTime - default service time (minutes)
 * @param {number} [intervalInMinutes=0] - interval (minutes)
 * @returns {string[]} formatted time array
 */
export const generateAvailableTimes = ({
  start,
  end,
  defaultTime,
  intervalInMinutes = 0,
}: {
  start: string;
  end: string;
  defaultTime: number;
  intervalInMinutes: number;
}) => {
  // Validate required parameters
  if (!start || !end || !defaultTime) return [];

  // Parse service time and interval
  const serviceTime = defaultTime;
  const interval = intervalInMinutes === 0 ? serviceTime : intervalInMinutes;

  // Create base date object (use current date as base)
  const baseDate = new Date();

  // Parse start and end time
  const startTime = parse(start, 'HH:mm', baseDate);
  const endTime = parse(end, 'HH:mm', baseDate);

  // Calculate service end time
  let currentStart = startTime;
  let currentEnd = addMinutes(currentStart, serviceTime);

  const availableTimes: string[] = [];

  // Generate time periods
  while (
    isBefore(currentEnd, endTime) ||
    (currentEnd.getHours() === endTime.getHours() &&
      currentEnd.getMinutes() === endTime.getMinutes())
  ) {
    // Format time (e.g. "9:30 AM")
    availableTimes.push(format(currentStart, 'h:mm a'));

    // Move to next time period
    currentStart = addMinutes(currentStart, interval);
    currentEnd = addMinutes(currentEnd, interval);
  }

  return availableTimes;
};

/**
 * Get ordinal suffix
 *
 * @param {number} num - number
 * @returns {string} ordinal suffix
 */
export const getOrdinalSuffix = (num: number) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

/**
 * Extract all error messages from nested validation errors (e.g., Formik errors)
 *
 * @param {any} errors - validation errors object that may contain nested structures
 * @returns {string[]} array of error message strings
 */
export const extractErrorMessages = (errors: any): string[] => {
  const messages: string[] = [];

  const traverse = (obj: any) => {
    if (typeof obj === 'string') {
      messages.push(obj);
    } else if (Array.isArray(obj)) {
      obj.forEach((item) => {
        if (item !== null && item !== undefined) {
          traverse(item);
        }
      });
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach((value) => {
        if (value !== null && value !== undefined) {
          traverse(value);
        }
      });
    }
  };

  traverse(errors);
  return messages;
};

/**
 * Get start of day UTC time
 *
 * @param {Date} date - date
 * @returns {number} start of day UTC time
 */
export const getStartOfDayUTCTime = (date: Date): number => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
  return Math.floor(utcDate.getTime() / 1000);
};

/**
 * Get end of day UTC time
 *
 * @param {Date} date - date
 * @returns {number} end of day UTC time
 */
export const getEndOfDayUTCTime = (date: Date): number => {
  const utcDate = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    )
  );
  return Math.floor(utcDate.getTime() / 1000);
};
